import { array, fn, hash } from '@ember/helper';
import Component from '@glimmer/component';
import { t } from 'ember-intl';
import sortableHandle from 'ember-sortable/modifiers/sortable-handle';
import BvDropdownMenu from 'volta/components/bv-dropdown-menu';
import BvIcon from 'volta/components/bv-icon';
import BvUnstyledBtn, { type BvUnstyledBtnSignature } from 'volta/components/bv-unstyled-btn';
import orElse from 'volta/helpers/or-else';
import cssVars from 'volta/modifiers/css-vars';

import type { TOC } from '@ember/component/template-only';
import type { THueVariationString } from 'volta/utils/style-utils';

interface BtnSignature {
  Args: {
    disabled?: boolean;
    color?: THueVariationString;
    onClick?: (event: PointerEvent) => void;
  };
  Element: HTMLButtonElement;
}

const Btn: TOC<BtnSignature> = <template>
  <BvUnstyledBtn
    @onClick={{@onClick}}
    disabled={{@disabled}}
    class='bv-dnd-handle'
    ...attributes
    {{sortableHandle}}
  >
    <BvIcon
      class='bv-dnd-handle__icon'
      @glyph='handler'
      @size='sm'
      @color={{if @disabled 'icon-disabled' (orElse @color 'icon')}}
    />
  </BvUnstyledBtn>
</template>;

export interface BvDndHandleSignature {
  Args: {
    disabled?: boolean;
    direction?: 'x' | 'y' | 'grid';
    color?: THueVariationString;
    onMoveNext?: () => void;
    onMovePrevious?: () => void;
  };
  Blocks: {
    default: [];
  };
  Element: BvUnstyledBtnSignature['Element'];
}

export default class BvDndHandle extends Component<BvDndHandleSignature> {
  get direction() {
    return this.args.direction ?? 'y';
  }

  get cursor() {
    return this.direction === 'x' ? 'ew-resize' : 'ns-resize';
  }

  get shouldDisplayMenu() {
    return (
      (this.args.onMoveNext != null || this.args.onMovePrevious != null) &&
      this.direction != 'grid' &&
      !this.args.disabled
    );
  }

  get nextLabel() {
    return this.direction === 'x' ? 'moveRight' : 'moveDown';
  }

  get nextIcon() {
    return this.direction === 'x' ? 'chevron-right' : 'chevron-down';
  }

  get previousIcon() {
    return this.direction === 'x' ? 'chevron-left' : 'chevron-up';
  }

  get previousLabel() {
    return this.direction === 'x' ? 'moveLeft' : 'moveUp';
  }

  onTogglePopover = (toggle: () => void, event: PointerEvent) => {
    event.stopPropagation();
    toggle();
  };

  <template>
    {{#if this.shouldDisplayMenu}}
      <BvDropdownMenu
        @sections={{array
          (hash
            items=(array
              (if
                @onMoveNext (hash content=(t this.nextLabel) onClick=@onMoveNext icon=this.nextIcon)
              )
              (if
                @onMovePrevious
                (hash content=(t this.previousLabel) onClick=@onMovePrevious icon=this.previousIcon)
              )
            )
          )
        }}
      >
        <:trigger as |popover|>
          <Btn
            @disabled={{@disabled}}
            @color={{@color}}
            @onClick={{fn this.onTogglePopover popover.toggle}}
            {{cssVars cursor=this.cursor}}
            {{popover.trigger}}
            ...attributes
          />
        </:trigger>
      </BvDropdownMenu>
    {{else}}
      <Btn @disabled={{@disabled}} @color={{@color}} {{cssVars cursor=this.cursor}} ...attributes />
    {{/if}}
  </template>
}
declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'BvDnd::Handle': typeof BvDndHandle;
    'bv-dnd/handle': typeof BvDndHandle;
  }
}
